import React, { useState, useEffect, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { FileSpreadsheet, X, Upload as UploadIcon } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { Spinner } from '../../../components/ui/Spinner';
import { usePincode } from '../../../hooks/usePincode';
import { pincodeService } from '../../../services/pincode';

export const UploadPincode: React.FC = () => {
  const [selectedCourier, setSelectedCourier] = useState<string>('');
  const [selectedSlab, setSelectedSlab] = useState<string>('All');
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Use the hook
  const {
    couriers = [],
    fetchCouriers,
    loading: loadingCouriers,
  } = usePincode();

  // Fetch all couriers on mount
  useEffect(() => {
    fetchCouriers();
  }, []);

  // Determine if selected courier is Xpressbees
  const selectedCourierObj = useMemo(() => {
    return couriers.find((c) => String(c.id) === String(selectedCourier));
  }, [couriers, selectedCourier]);

  const isXpressbees = useMemo(() => {
    if (!selectedCourierObj) return false;
    const name = selectedCourierObj.name.toLowerCase();
    return name.includes('xpressbees') || name.includes('xb') || String(selectedCourierObj.id) === '1';
  }, [selectedCourierObj]);

  // Standard slabs vs Xpressbees specific slabs
  const standardSlabOptions = ['0.5 Kg', '2 Kg', '5 Kg', '10 Kg', 'All'];
  const xbSlabOptions = [
    { label: 'Under 5 Kg', value: 'BELOW_5_KG' },
    { label: 'Over 5 Kg', value: 'ABOVE_5_KG' },
  ];

  // Handler for courier dropdown change
  const handleCourierChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const courierId = e.target.value;
    setSelectedCourier(courierId);

    const cObj = couriers.find((c) => String(c.id) === String(courierId));
    const isXB = cObj
      ? cObj.name.toLowerCase().includes('xpressbees') ||
        cObj.name.toLowerCase().includes('xb') ||
        String(cObj.id) === '1'
      : false;

    if (isXB) {
      setSelectedSlab('BELOW_5_KG');
    } else {
      setSelectedSlab('All');
    }
  };

  // Handler for file input
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const validExtensions = ['.xlsx', '.xls'];
      const fileExt = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();
      if (!validExtensions.includes(fileExt)) {
        toast.error('Please upload an Excel file (.xlsx or .xls)');
        return;
      }
      setFile(selectedFile);
    }
  };

  // Handler to download sample excel matching the required format
  const handleDownloadSample = () => {
    const sampleData = [
      ['PinCode', 'Hub Name', 'Hub City', 'Hub State', 'Hub Zone Name', 'Status'],
      ['524322', 'AS PETA', 'A S PETA', 'ANDHRA PRADESH', 'South-1', 'Good to Go'],
      ['524304', 'AS PETA', 'A S PETA', 'ANDHRA PRADESH', 'South-1', 'Good to Go'],
      ['493881', 'Rajim', 'ABHANPUR', 'CHHATTISGARH', 'Central', 'Good to Go'],
      ['493885', 'Rajim', 'ABHANPUR', 'CHHATTISGARH', 'Central', 'Good to Go'],
      ['493887', 'Rajim', 'ABHANPUR', 'CHHATTISGARH', 'Central', 'Good to Go'],
      ['152116', 'Abohar', 'Abohar', 'PUNJAB', 'North', 'PDD+1'],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'ServiceablePincodes');
    const fileName = isXpressbees
      ? `xpressbees_${selectedSlab.toLowerCase()}_sample.xlsx`
      : 'pincode_serviceability_sample.xlsx';
    XLSX.writeFile(workbook, fileName);
    toast.success('Sample Excel downloaded successfully');
  };

  // Handler for upload execution
  const handleUploadPincodes = async () => {
    if (!selectedCourier) {
      toast.error('Please select a courier first!');
      return;
    }
    if (!file) {
      toast.error('Please select an Excel file to upload!');
      fileInputRef.current?.click();
      return;
    }

    const weightCategory = isXpressbees ? selectedSlab : selectedSlab;

    setIsUploading(true);
    try {
      const response = await pincodeService.uploadPincodeServiceabilityExcel(
        file,
        selectedCourier,
        weightCategory
      );

      toast.success(response?.message || 'Pincode serviceability Excel uploaded successfully.');
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to upload pincode serviceability Excel.';
      toast.error(errorMsg);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="py-2 lg:py-4">
      {/* Hidden File Input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".xlsx, .xls"
        className="hidden"
      />

      {/* Header */}
      <div className="space-y-2 mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-text">Upload Pincodes</h1>
      </div>

      {/* Card */}
      <div className="bg-card rounded-[16px] shadow-sm border p-6">
        <div className="flex flex-col gap-8">
          {/* Title */}
          <div className="border-b pb-4">
            <h2 className="text-xl font-semibold mb-1">
              Upload Serviceable Pin-code
            </h2>
            <p className="text-sm text-gray-600">Active pin-code list</p>
          </div>

          {/* Form */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Left Column: Slabs */}
            <div className="space-y-8">
              <div className="flex flex-col gap-3">
                <label className="font-medium text-gray-900">Slab</label>
                <div className="flex flex-wrap gap-3">
                  {isXpressbees ? (
                    // When Xpressbees is selected: hide 0.5kg, 2kg, 5kg, 10kg, All; show Under 5 Kg & Over 5 Kg
                    xbSlabOptions.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setSelectedSlab(opt.value)}
                        className={`px-4 py-2 text-sm font-medium rounded-md border transition-all ${
                          selectedSlab === opt.value
                            ? 'border-accent bg-accent text-white'
                            : 'border-gray-300 text-gray-700 hover:border-gray-400 bg-white'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))
                  ) : (
                    // Other couriers or unselected: show default standard slabs
                    standardSlabOptions.map((slab) => (
                      <button
                        key={slab}
                        type="button"
                        onClick={() => setSelectedSlab(slab)}
                        className={`px-4 py-2 text-sm font-medium rounded-md border transition-all ${
                          selectedSlab === slab
                            ? 'border-accent bg-accent text-white'
                            : 'border-gray-300 text-gray-700 hover:border-gray-400 bg-white'
                        }`}
                      >
                        {slab}
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Right Column: Select Courier */}
            <div className="space-y-8">
              <Select
                label="Select Courier"
                required
                value={selectedCourier}
                onChange={handleCourierChange}
                placeholder={loadingCouriers ? 'Loading couriers...' : 'Select Courier'}
                options={couriers.map((c) => ({ label: c.name, value: String(c.id) }))}
              />
            </div>
          </div>

          {/* Selected File Feedback */}
          {file && (
            <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg max-w-md mx-auto w-full">
              <div className="flex items-center gap-2 truncate">
                <FileSpreadsheet className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                <span className="text-sm font-medium text-gray-700 truncate">{file.name}</span>
                <span className="text-xs text-gray-500 flex-shrink-0">
                  ({(file.size / 1024).toFixed(1)} KB)
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className="p-1 hover:bg-gray-200 rounded text-gray-500 hover:text-red-500 transition-colors"
                title="Remove file"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Action Buttons - Download Sample and Upload */}
          <div className="flex flex-col lg:flex-row gap-4 justify-center pt-6">
            <Button
              variant="outline"
              type="button"
              onClick={handleDownloadSample}
              className="px-10 py-3 border-gray-300 text-gray-700 hover:bg-gray-200 hover:text-text3"
            >
              Download Sample
            </Button>
            <Button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="px-10 py-3 bg-primary hover:bg-primary-dark flex items-center gap-2 text-white"
            >
              <UploadIcon className="w-4 h-4" />
              {file ? 'Change File' : 'Upload'}
            </Button>
          </div>

          {/* Upload Pincodes Button */}
          <div className="flex justify-center pt-2">
            <Button
              type="button"
              className="px-10 py-3 bg-accent hover:bg-accent-dark text-white font-medium flex items-center gap-2"
              onClick={handleUploadPincodes}
              disabled={isUploading}
            >
              {isUploading && <Spinner size="sm" className="text-white" />}
              {isUploading ? 'Uploading Pincodes...' : 'Upload Pincodes'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadPincode;